import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import client from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CalendarBlank, Camera, MapPin, CheckCircle, ArrowRight, Warning, Star, SealCheck, Quotes, ShieldCheck, Crown, VideoCamera, Sparkle, House } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";
import { celebrate } from "../lib/celebrate";
import { TIME_SLOTS, slotAvailable, reeltyPackagePrice, REELTY_PRICING } from "../lib/helpers";
import { QuestProgress } from "../components/ui/quest-progress";
import { captureRef } from "../lib/referral";

const LAND_STEPS = [{ k: "addr", t: "Address" }, { k: "pkg", t: "Package" }, { k: "time", t: "Schedule" }, { k: "you", t: "Confirm" }];

const PACKAGES_NOTE = "Penthouse Package (All-Inclusive) $495 · Photo Only $279";

function StarRow({ value, size = 15 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} weight={n <= value ? "fill" : "regular"}
          className={n <= value ? "text-[#B08D57]" : "text-[#CFC7B8]"} />
      ))}
    </div>
  );
}

export default function PhotographerLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    street: "", city: "", state: "", zip: "", lat: null, lon: null,
    shoot_date: "", shoot_time: "", package_type: "All-Inclusive", drone: false, interior: true,
    agent_name: "", agent_email: "", agent_phone: "", notes: "",
  });
  const [addrQuery, setAddrQuery] = useState("");
  const [sugs, setSugs] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const activeRef = useRef(true);

  useEffect(() => {
    captureRef(slug);
    client.get(`/public/photographer/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    const q = addrQuery.trim();
    if (q.length < 3) { setSugs([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await client.get("/public/geo/autocomplete", { params: { q } });
        if (active) { setSugs(data.results || []); setShowSug(true); }
      } catch { if (active) setSugs([]); }
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [addrQuery]);

  const set = (k, v) => setForm((f) => {
    const n = { ...f, [k]: v };
    if (["street", "city", "state", "zip"].includes(k)) { n.lat = null; n.lon = null; }
    return n;
  });

  const pick = (s) => {
    setForm((f) => ({ ...f, street: s.street, city: s.city, state: s.state, zip: s.zip, lat: s.lat ?? null, lon: s.lon ?? null }));
    setAddrQuery(s.label); setSugs([]); setShowSug(false);
  };

  const price = form.package_type ? reeltyPackagePrice(form.package_type, form.interior) : 0;
  const landStep = !form.street ? 0 : !form.shoot_date ? 2 : 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.street || !form.city || !form.state || !form.zip) return toast.error("Please enter a full property address");
    if (!form.package_type) return toast.error("Please choose a package");
    if (!form.agent_email) return toast.error("Please enter your email");
    setSaving(true);
    try {
      const r = await client.post(`/public/book/${slug}`, form);
      celebrate();
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book — please try again");
    } finally { setSaving(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#FBFAF7] flex items-center justify-center p-6 grain">
      <div className="text-center" data-testid="landing-notfound">
        <Camera size={40} className="mx-auto text-[#4A0000] mb-3" weight="duotone" />
        <h1 className="font-display text-3xl text-[#1A1A1A]">Booking page not found</h1>
        <p className="text-[#5C5A58] mt-2">This photographer link isn't available.</p>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen bg-[#FBFAF7] flex items-center justify-center font-black text-2xl text-[#4A0000] tracking-tight">REELTY</div>;

  if (done) return (
    <div className="min-h-screen bg-[#FBFAF7] flex items-center justify-center p-6 grain">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-md text-center bg-white border border-[#E9DDC6] rounded-2xl p-10 shadow-premium-lg" data-testid="landing-success">
        <CheckCircle size={56} className="mx-auto text-[#4A0000] mb-4" weight="fill" />
        <h1 className="font-display text-3xl text-[#1A1A1A]">You're booked!</h1>
        <p className="text-[#5C5A58] mt-3">{done.message}</p>
        <div className="mt-6 bg-[#FBFAF7] border border-[#E9DDC6] rounded-lg p-4 text-center text-sm space-y-3">
          <div className="flex flex-col items-center py-1"><span className="text-[#5C5A58]">Photographer</span><span className="font-medium">{done.photographer_name}</span></div>
          <div className="flex flex-col items-center py-1"><span className="text-[#5C5A58]">Package total</span><span className="font-display text-lg text-[#4A0000]">${done.package_price}</span></div>
        </div>
      </motion.div>
    </div>
  );

  const heroImg = (data.gallery && data.gallery[0]) || "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?w=1600&q=80";

  return (
    <div className="min-h-screen bg-[#FBFAF7] grain">
      {/* ---------- Hero ---------- */}
      <div className="relative text-[#FBFAF7] overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: "easeOut" }}
          src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="landing-hero-bg" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A0A] via-[#4A0000]/92 to-[#4A0000]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A0A0A]/70 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 pt-10 pb-16 sm:pt-14 sm:pb-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] text-[#E7C79A]">
            <span className="font-black tracking-tight text-[#FBFAF7] text-sm">REELTY</span>
            <span className="opacity-50">·</span>
            <span className="inline-flex items-center gap-1"><SealCheck size={14} weight="fill" className="text-[#B08D57]" />VERIFIED PHOTOGRAPHER</span>
          </motion.div>

          <div className="flex items-end gap-6 mt-8 flex-wrap">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-[#B08D57] flex items-center justify-center text-[#4A0000] text-4xl font-bold overflow-hidden shrink-0 shadow-xl ring-4 ring-white/10">
              <span className="absolute inset-0 flex items-center justify-center">{(data.name || "R")[0].toUpperCase()}</span>
              {data.avatar && <img src={data.avatar} alt={data.name} onError={(e) => { e.currentTarget.style.display = "none"; }} className="relative w-full h-full object-cover" />}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }} className="min-w-0">
              <p className="eyebrow text-[#E7C79A]">BOOK A SHOOT WITH</p>
              <h1 className="font-display text-4xl sm:text-6xl leading-[0.95] mt-1.5" data-testid="landing-name">{data.name}</h1>
              <div className="flex items-center gap-2.5 mt-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 rounded-[5px] bg-white/12 backdrop-blur-sm border border-white/15 px-3 py-1 text-sm" data-testid="landing-houses">
                  <Camera size={15} weight="duotone" className="text-[#E7C79A]" /> {data.houses_shot} {data.houses_shot === 1 ? "home" : "homes"} shot
                </span>
                {data.rating_count > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-[5px] bg-white/12 backdrop-blur-sm border border-white/15 px-3 py-1 text-sm" data-testid="landing-rating">
                    <Star size={15} weight="fill" className="text-[#E7C79A]" /> {data.rating_avg} · {data.rating_count} {data.rating_count === 1 ? "review" : "reviews"}
                  </span>
                )}
                {data.tier && (
                  <span className="inline-flex items-center rounded-[5px] bg-[#B08D57] text-[#4A0000] font-semibold px-3 py-1 text-sm shadow-sm">{data.tier}</span>
                )}
              </div>
            </motion.div>
          </div>
        </div>
        {/* soft cream fade into the body */}
        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#FBFAF7] to-transparent" />
      </div>

      {/* ---------- Body: content + sticky booking card ---------- */}
      <div className="max-w-6xl mx-auto px-6 py-10 lg:py-14 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-start">
        {/* LEFT: story + trust (order-2 on mobile so the form sits right under the hero) */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="order-2 lg:order-1 space-y-12">
          {data.bio && (
            <div>
              <p className="eyebrow text-[#B08A2E] mb-2">About</p>
              <p className="text-[#1A1A1A] text-lg leading-relaxed" data-testid="landing-bio">{data.bio}</p>
            </div>
          )}

          {/* Reviews */}
          {data.reviews && data.reviews.length > 0 && (
            <div data-testid="landing-reviews">
              <div className="flex items-center justify-between mb-4">
                <p className="eyebrow text-[#B08A2E]">What agents say</p>
                {data.rating_count > 0 && (
                  <span className="inline-flex items-center gap-2 rounded-[5px] border border-[#E9DDC6] bg-white px-3 py-1 shadow-premium">
                    <StarRow value={Math.round(data.rating_avg)} />
                    <span className="text-sm font-semibold text-[#4A0000]">{data.rating_avg}</span>
                    <span className="text-xs text-[#8A8782]">({data.rating_count})</span>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.reviews.map((r, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="relative bg-white border border-[#E9DDC6] rounded-2xl p-5 shadow-premium hover:shadow-premium-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200" data-testid={`landing-review-${i}`}>
                    <Quotes size={26} weight="fill" className="text-[#B08D57]/25 absolute top-4 right-4" />
                    <StarRow value={r.rating} />
                    {r.text && <p className="text-[15px] text-[#1A1A1A] leading-relaxed mt-3">{r.text}</p>}
                    <p className="text-xs text-[#8A8782] mt-4 font-medium">— {r.agent_name}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Recent work gallery */}
          {data.gallery && data.gallery.length > 1 && (
            <div>
              <p className="eyebrow text-[#B08A2E] mb-4">Recent work</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="landing-gallery">
                {data.gallery.slice(0, 6).map((src, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="aspect-[4/3] overflow-hidden rounded-xl border border-[#E9DDC6] bg-[#FAF9F6] shadow-premium group">
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* RIGHT: sticky booking card (order-1 on mobile) */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
          className="order-1 lg:order-2 lg:sticky lg:top-8 self-start">
          <div className="bg-white border border-[#E9DDC6] rounded-3xl shadow-premium-lg overflow-hidden" data-testid="landing-form-card">
            <div className="bg-[#4A0000] text-[#FBFAF7] px-6 py-5">
              <h2 className="font-display text-2xl">Schedule your shoot</h2>
              <p className="text-[#FBFAF7]/75 text-sm mt-0.5">Book {data.name} in under a minute.</p>
            </div>
            <div className="px-6 pt-5 pb-1 bg-[#FBFAF7] border-b border-[#F0ECE2]">
              <QuestProgress steps={LAND_STEPS} step={landStep} testid="landing-quest" className="pb-3" />
            </div>
            <form onSubmit={submit} className="p-6 space-y-5" data-testid="landing-form">
              <div className="relative">
                <Label>Property address <span className="text-[#4A0000]">*</span></Label>
                <Input data-testid="landing-address-search" value={addrQuery} autoComplete="off"
                  onChange={(e) => { setAddrQuery(e.target.value); setShowSug(true); }}
                  placeholder="Start typing an address…" className="bg-white border-[#E9DDC6]" />
                {showSug && sugs.length > 0 && (
                  <div data-testid="landing-suggestions" className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E9DDC6] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {sugs.map((s, i) => (
                      <button key={i} type="button" data-testid={`landing-suggestion-${i}`} onClick={() => pick(s)}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F5F1E8] border-b border-[#F0ECE2] last:border-0">{s.label}</button>
                    ))}
                  </div>
                )}
                {form.street && <p className="text-xs text-[#5C5A58] mt-1 flex items-center gap-1"><MapPin size={13} weight="fill" className="text-[#4A0000]" /> {form.street}, {form.city}, {form.state} {form.zip}</p>}
              </div>

              <div data-testid="landing-package-picker">
                <Label>Your package <span className="text-[#4A0000]">*</span></Label>
                {form.package_type !== "Photo Only" ? (
                  <>
                    <div data-testid="landing-package" className="relative w-full rounded-xl border p-4 mt-1.5 overflow-hidden border-[#4A0000] bg-[#4A0000] text-white">
                      <span className="absolute top-0 right-0 bg-[#B08D57] text-[#1A1715] text-[10px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-bl-lg">Most booked</span>
                      <div className="flex items-center gap-2.5">
                        <span className="grid place-items-center w-10 h-10 rounded-full bg-[#B08D57]/20"><Camera size={22} weight="fill" className="text-[#B08D57]" /></span>
                        <div>
                          <div className="font-display text-lg leading-none">Penthouse Package</div>
                          <div className="text-[10px] tracking-[0.16em] uppercase mt-1 text-[#B08D57]">All-Inclusive</div>
                        </div>
                        <div className="ml-auto font-display text-2xl text-[#B08D57]" data-testid="landing-price">${price}</div>
                      </div>
                      <div className="mt-3.5 grid gap-1.5 text-sm text-white/85">
                        <span className="flex items-center gap-2"><CheckCircle size={15} weight="fill" className="text-[#B08D57] shrink-0" />Interior &amp; exterior photography</span>
                        <span className="flex items-center gap-2"><VideoCamera size={15} weight="fill" className="text-[#B08D57] shrink-0" />Cinematic video — branded &amp; unbranded</span>
                        <span className="flex items-center gap-2"><Sparkle size={15} weight="fill" className="text-[#B08D57] shrink-0" />Virtual staging &amp; twilight edits</span>
                      </div>
                    </div>
                    <div className="mt-2 rounded-lg border border-[#B08D57]/45 bg-[#B08D57]/10 px-3 py-2.5 flex items-start gap-2.5" data-testid="landing-partner-callout">
                      <Crown size={18} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-[#7a5c1a]"><b className="text-[#4A0000]">Your first shoot's 360° tour + drone are free</b> — a Penthouse Partner welcome, 1st month on us. A total no-brainer.</p>
                    </div>
                    {form.package_type === "All-Inclusive" && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer mt-2 rounded-lg border border-[#E9DDC6] bg-[#FBFAF7] px-3 py-2 hover:border-[#B08D57] transition-colors" data-testid="landing-interior-label">
                        <input type="checkbox" data-testid="landing-interior" checked={!form.interior} onChange={(e) => set("interior", !e.target.checked)} className="w-4 h-4 accent-[#4A0000]" />
                        Skip interior — drone / exterior only <span className="text-[#2e7d32] font-medium">(save ${REELTY_PRICING.noInterior})</span>
                      </label>
                    )}
                    <button type="button" data-testid="landing-only-photos" onClick={() => set("package_type", "Photo Only")}
                      className="mt-2.5 inline-flex items-center gap-1 text-sm text-[#5C5A58] hover:text-[#4A0000] underline decoration-dotted underline-offset-4 transition-colors">
                      <House size={14} weight="duotone" />Only need photos? Switch to Photo Only — ${REELTY_PRICING.photoOnly}
                    </button>
                  </>
                ) : (
                  <>
                    <div data-testid="landing-package" className="relative w-full rounded-xl border p-4 mt-1.5 border-[#4A0000] bg-[#4A0000] text-white">
                      <div className="flex items-center gap-2.5">
                        <span className="grid place-items-center w-10 h-10 rounded-full bg-[#B08D57]/20"><House size={22} weight="fill" className="text-[#B08D57]" /></span>
                        <div>
                          <div className="font-display text-lg leading-none">Photo Only</div>
                          <div className="text-[11px] mt-1 text-white/70">30–50 professional photos</div>
                        </div>
                        <div className="ml-auto font-display text-2xl text-[#B08D57]" data-testid="landing-price">${price}</div>
                      </div>
                    </div>
                    <button type="button" data-testid="landing-back-penthouse" onClick={() => set("package_type", "All-Inclusive")}
                      className="mt-2.5 inline-flex items-center gap-1 text-sm text-[#4A0000] hover:text-[#5C1A1A] font-medium transition-colors">
                      <Crown size={14} weight="fill" className="text-[#B08D57]" />Get video, staging + free 360 &amp; drone → Penthouse Package
                    </button>
                  </>
                )}
              </div>

              <div>
                <Label>Preferred date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="landing-date"
                      className="w-full h-10 flex items-center gap-2 rounded-md border border-[#E9DDC6] bg-white px-3 text-sm text-left hover:border-[#4A0000] transition-colors">
                      <CalendarBlank size={16} weight="duotone" className="text-[#4A0000] shrink-0" />
                      <span className={form.shoot_date ? "text-[#1A1A1A]" : "text-[#9A968E]"}>
                        {form.shoot_date ? format(new Date(`${form.shoot_date}T00:00:00`), "EEEE, MMMM d, yyyy") : "Pick a date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#E9DDC6]" align="start" data-testid="landing-date-popover">
                    <Calendar mode="single"
                      selected={form.shoot_date ? new Date(`${form.shoot_date}T00:00:00`) : undefined}
                      onSelect={(d) => { if (d) { set("shoot_date", format(d, "yyyy-MM-dd")); set("shoot_time", ""); } }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || (data.blackout_dates || []).includes(format(d, "yyyy-MM-dd")) || !TIME_SLOTS.some((s) => slotAvailable(data.availability, d.getDay(), s.value))}
                      initialFocus />
                  </PopoverContent>
                </Popover>
                {(data.blackout_dates || []).length > 0 && (
                  <p className="text-xs text-[#5C5A58] mt-1.5" data-testid="landing-blackout-note">Days {data.name} is unavailable can't be selected.</p>
                )}
              </div>

              {form.shoot_date && (
                <div data-testid="landing-time-section">
                  <Label>Preferred time <span className="text-[#5C5A58] font-normal text-xs">(ET, 2-hour slot)</span></Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {TIME_SLOTS.map((s) => {
                      const wd = new Date(`${form.shoot_date}T00:00:00`).getDay();
                      const avail = slotAvailable(data.availability, wd, s.value);
                      const selected = form.shoot_time === s.value;
                      return (
                        <button key={s.value} type="button" data-testid={`landing-time-${s.value}`}
                          disabled={!avail}
                          onClick={() => avail && set("shoot_time", s.value)}
                          title={avail ? "" : `${data.name} is unavailable at this time`}
                          className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors text-left ${
                            selected ? "bg-[#4A0000] text-white border-[#4A0000]"
                            : avail ? "bg-white border-[#E9DDC6] text-[#1A1A1A] hover:border-[#4A0000]"
                            : "bg-[#FAF9F6] border-[#E9DDC6] text-[#B8B4AC] line-through cursor-not-allowed"}`}>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#5C5A58] mt-2" data-testid="landing-availability-note">Grayed-out times are unavailable on {data.name}'s schedule.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Your name</Label><Input data-testid="landing-agent-name" value={form.agent_name} onChange={(e) => set("agent_name", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
                <div><Label>Phone</Label><Input data-testid="landing-agent-phone" value={form.agent_phone} onChange={(e) => set("agent_phone", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
              </div>
              <div><Label>Email <span className="text-[#4A0000]">*</span></Label><Input data-testid="landing-agent-email" type="email" required value={form.agent_email} onChange={(e) => set("agent_email", e.target.value)} placeholder="you@brokerage.com" className="bg-white border-[#E9DDC6]" /><p className="text-xs text-[#5C5A58] mt-1">We'll email you a link to sign in and manage your booking.</p></div>
              <div><Label>Notes</Label><Input data-testid="landing-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Gate code, access details, etc." className="bg-white border-[#E9DDC6]" /></div>

              <Button data-testid="landing-submit" type="submit" disabled={saving} className="w-full h-12 bg-[#4A0000] hover:bg-[#5C1A1A] text-white text-base rounded-[5px] shadow-premium">
                {saving ? "Booking…" : <>Book this shoot <ArrowRight size={18} className="ml-1" /></>}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-[#8A8782]"><ShieldCheck size={14} weight="duotone" className="text-[#B08D57]" />No payment due now · secure checkout after confirmation</p>
            </form>
          </div>
        </motion.div>
      </div>

      <p className="text-center text-xs text-[#9A968E] pb-10">Powered by <span className="font-bold tracking-wider text-[#4A0000]">REELTY</span></p>
    </div>
  );
}
